import React, { FC, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import CreateNewSubmissionCard from '../components/CreateNewSubmissionCard';

import { CertificateSubmission } from '../../model';
import { Customer } from '../../../salesEstimateModules/estimateDetails/model';

import axios from '../../../sharedModules/utils/axios.utils';

const CustomerDetailPage: FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const history = useHistory();

  const [customer, setCustomer] = useState<Customer>({});
  const [pastSubmissions, setPastSubmissions] = useState<CertificateSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchCustomer = async () => {
    try {
      let response: any = await axios.get(`energy_solutions/energy_solutions/${customerId}`, {
        params: { model_class: 'Customer' },
      });
      setCustomer(response.data.result);
    } catch (error) {
      setErrorMessage('Failed to load customer details');
    }
  };

  const fetchPastSubmissions = async () => {
    try {
      let response: any = await axios.get('energy_solutions/certificate_submissions/customer_submissions', {
        params: { customer_id: customerId },
      });
      setPastSubmissions(response.data.result || []);
    } catch (error) {
      console.error('Error fetching past submissions:', error);
    }
  };

  const handleSubmissionClick = (submissionId: number) => {
    history.push(`/energy_solutions/submissions/${submissionId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchCustomer();
      await fetchPastSubmissions();
      setLoading(false);
    };

    if (customerId) {
      loadData();
    }
  }, [customerId]);

  const defaultProjectName = () => `${customer.name} - ${new Date().toLocaleDateString()}`;

  return (
    <Grid container rowSpacing={3}>
      <LoadingWrapper isLoading={loading}>
        <Grid item xs={12}>
          <PageHeader
            mainTitle="Energy Solutions - Customer Detail"
            subTitle={customer?.name}
            backButtonLabel="Customer Lookup"
            backButtonLink="/energy_solutions/customer_lookup"
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <CreateNewSubmissionCard customerId={customerId} defaultProjectName={defaultProjectName()} />
        </Grid>

        {/* Past Submissions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Past Submissions
              </Typography>

              {pastSubmissions.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Project Name</TableCell>
                        <TableCell>Created Date</TableCell>
                        <TableCell>Certificates</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pastSubmissions.map((submission) => (
                        <TableRow key={submission.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {submission.submission_name}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDate(submission.created_at)}</TableCell>
                          <TableCell>
                            <Chip
                              label={`${submission.ahri_certificates?.length || 0} certificates`}
                              size="small"
                              color={submission.ahri_certificates?.length > 0 ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleSubmissionClick(Number(submission.id))}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No past submissions found for this customer.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {errorMessage && (
          <Grid item xs={12}>
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          </Grid>
        )}
      </LoadingWrapper>
    </Grid>
  );
};

export default CustomerDetailPage;
